import type { MultiSelectChrome } from '@/lib/transforms/types/multi-select-chrome'
import type { ShapeBounds } from '@/lib/shapes/types/shape-bounds'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  boxVerticesForShapeGeometry,
  canvasVecToLocal,
  localVecToCanvas,
} from '@/lib/transforms/geometry'
import type { UnifiedSelectionResizeHandle } from '@/lib/transforms/types/unified-selection-resize-handle'
import { computeUnifiedSelectionOrientedResizeLocal } from '@/lib/transforms/unified-selection-oriented-resize'
import type { MultiSelectResizeShapeSnap } from './types/multi-select-resize-shape-snap'
import { MIN_DIMENSION } from './constants/multi-resize-min-dimension'
import { getMultiResizeFixedAnchorLocal } from './helpers/multi-resize-local-frame'
import { applyMultiResizeCoverageCorrection } from './helpers/multi-resize-coverage-correction'

export function computeMultiSelectResize(
  chromeStart: MultiSelectChrome,
  localInitial: { x: number; y: number; width: number; height: number },
  shapeInitials: Record<string, MultiSelectResizeShapeSnap>,
  direction: UnifiedSelectionResizeHandle,
  localDx: number,
  localDy: number,
  shiftKey: boolean,
  aspectLock: { current?: 'width' | 'height' },
  panelDimensionsLinked: boolean,
  /** See `shouldUseAspectRatio` in unified-selection-oriented-resize.ts — forces aspect lock on a
   *  corner drag regardless of Shift when any shape in the selection has a photo fill, matching
   *  legacy's own photo-fill resize behavior. */
  hasPhotoFill = false
): { nextChrome: MultiSelectChrome; updates: Record<string, Partial<ShapeData>> } {
  const w0 = localInitial.width
  const h0 = localInitial.height
  const rot = chromeStart.rotationDeg
  const initialShape = {
    x: localInitial.x,
    y: localInitial.y,
    width: localInitial.width,
    height: localInitial.height,
  }
  const { next } = computeUnifiedSelectionOrientedResizeLocal({
    direction,
    initial: initialShape,
    dx: localDx,
    dy: localDy,
    shiftKey,
    panelDimensionsLinked,
    minDimension: MIN_DIMENSION,
    aspectLock,
    hasPhotoFill,
  })

  const sx = next.width / w0
  const sy = next.height / h0

  const anchorLocal = getMultiResizeFixedAnchorLocal(direction, w0, h0)
  const anchorCanvas = {
    x: chromeStart.pivotX + localVecToCanvas(anchorLocal, rot).x,
    y: chromeStart.pivotY + localVecToCanvas(anchorLocal, rot).y,
  }

  const updates: Record<string, Partial<ShapeData>> = {}

  for (const id of Object.keys(shapeInitials)) {
    const init = shapeInitials[id]
    const resizedVertices = boxVerticesForShapeGeometry({
      x: init.x,
      y: init.y,
      width: init.width,
      height: init.height,
      rotation: init.rotation ?? 0,
    }).map((vertex) => {
      const fromAnchorCanvas = {
        x: vertex.x - anchorCanvas.x,
        y: vertex.y - anchorCanvas.y,
      }
      const fromAnchorLocal = canvasVecToLocal(fromAnchorCanvas, rot)
      const scaledLocal = {
        x: fromAnchorLocal.x * sx,
        y: fromAnchorLocal.y * sy,
      }
      const scaledCanvas = localVecToCanvas(scaledLocal, rot)

      return {
        x: anchorCanvas.x + scaledCanvas.x,
        y: anchorCanvas.y + scaledCanvas.y,
      }
    })
    const centroid = resizedVertices.reduce(
      (acc, point) => ({ x: acc.x + point.x, y: acc.y + point.y }),
      { x: 0, y: 0 }
    )
    const centerGuess = {
      x: centroid.x / Math.max(1, resizedVertices.length),
      y: centroid.y / Math.max(1, resizedVertices.length),
    }
    let minLocalX = Infinity
    let maxLocalX = -Infinity
    let minLocalY = Infinity
    let maxLocalY = -Infinity

    for (const vertex of resizedVertices) {
      const local = canvasVecToLocal(
        { x: vertex.x - centerGuess.x, y: vertex.y - centerGuess.y },
        init.rotation ?? 0
      )

      minLocalX = Math.min(minLocalX, local.x)
      maxLocalX = Math.max(maxLocalX, local.x)
      minLocalY = Math.min(minLocalY, local.y)
      maxLocalY = Math.max(maxLocalY, local.y)
    }

    const localCenterOffset = {
      x: (minLocalX + maxLocalX) / 2,
      y: (minLocalY + maxLocalY) / 2,
    }
    const centerOffsetCanvas = localVecToCanvas(localCenterOffset, init.rotation ?? 0)
    const center = {
      x: centerGuess.x + centerOffsetCanvas.x,
      y: centerGuess.y + centerOffsetCanvas.y,
    }
    const newW = Math.max(MIN_DIMENSION, maxLocalX - minLocalX)
    const newH = Math.max(MIN_DIMENSION, maxLocalY - minLocalY)

    updates[id] = {
      x: center.x - newW / 2,
      y: center.y - newH / 2,
      width: newW,
      height: newH,
    }
  }

  const centerLocal = {
    x: next.x + next.width / 2,
    y: next.y + next.height / 2,
  }
  const pivotOffset = localVecToCanvas(centerLocal, rot)

  const nextChrome: MultiSelectChrome = {
    pivotX: chromeStart.pivotX + pivotOffset.x,
    pivotY: chromeStart.pivotY + pivotOffset.y,
    width: next.width,
    height: next.height,
    rotationDeg: chromeStart.rotationDeg,
  }

  return { nextChrome, updates }
}

/**
 * Same oriented multi-select resize as dragging the chrome handles, driven by target chrome W/H
 * from the object panel (not axis-aligned union scaling of placement rects).
 */
export function computeShapesOnlyMultiSelectResizeFromPanelTargets(params: {
  chrome: MultiSelectChrome
  shapeInitials: Record<string, MultiSelectResizeShapeSnap>
  boundsInitials: Record<string, ShapeBounds | undefined>
  targetChromeWidth: number
  targetChromeHeight: number
  field: 'width' | 'height'
  dimensionsLinked: boolean
}): { nextChrome: MultiSelectChrome; updates: Record<string, Partial<ShapeData>> } | null {
  const {
    chrome,
    shapeInitials,
    boundsInitials,
    targetChromeWidth,
    targetChromeHeight,
    field,
    dimensionsLinked,
  } = params
  const w0 = chrome.width
  const h0 = chrome.height

  if (!Number.isFinite(w0) || !Number.isFinite(h0) || w0 <= 0 || h0 <= 0) {
    return null
  }

  const tw = Math.max(MIN_DIMENSION, targetChromeWidth)
  const th = Math.max(MIN_DIMENSION, targetChromeHeight)

  const localInitial = {
    x: -w0 / 2,
    y: -h0 / 2,
    width: w0,
    height: h0,
  }

  let direction: UnifiedSelectionResizeHandle
  let localDx = 0
  let localDy = 0

  if (dimensionsLinked) {
    const s = field === 'width' ? tw / w0 : th / h0

    direction = 'se'
    localDx = w0 * (s - 1)
    localDy = h0 * (s - 1)
  } else if (field === 'width') {
    direction = 'e'
    localDx = tw - w0
    localDy = 0
  } else {
    direction = 's'
    localDx = 0
    localDy = th - h0
  }

  const aspectLock: { current?: 'width' | 'height' } = {}
  const { nextChrome: rawChrome, updates } = computeMultiSelectResize(
    chrome,
    localInitial,
    shapeInitials,
    direction,
    localDx,
    localDy,
    false,
    aspectLock,
    dimensionsLinked
  )

  const correctedChrome = applyMultiResizeCoverageCorrection(
    rawChrome,
    direction,
    shapeInitials,
    boundsInitials,
    updates
  )

  return { nextChrome: correctedChrome, updates }
}
